import { useQueryClient } from '@tanstack/react-query';

import {
  getApiV1PersonIdAttachmentsQueryKey,
  useDeleteApiV1PersonIdAttachmentsAttachmentid,
  useGetApiV1PersonIdAttachments,
  usePostApiV1PersonIdAttachments,
} from '@/shared/api/generated/hooks';

/**
 * Hook para gerenciar anexos de documentos de uma pessoa.
 *
 * Encapsula listagem, upload e remoção de anexos via
 * `GET/POST/DELETE /api/v1/person/{id}/attachments`.
 *
 * @param personId - ID da pessoa. Quando `undefined` (modo criação), as operações são desabilitadas.
 * @returns Lista de anexos com estado de loading/error e callbacks de upload/remoção.
 */
export function usePersonAttachments(personId: number | undefined) {
  const queryClient = useQueryClient();

  const invalidate = () => {
    if (personId) {
      queryClient.invalidateQueries({ queryKey: getApiV1PersonIdAttachmentsQueryKey(personId) });
    }
  };

  const {
    data: attachments = [],
    isLoading,
    isError,
  } = useGetApiV1PersonIdAttachments(personId ?? 0, { query: { enabled: !!personId } });

  const { mutate: uploadAttachment, isPending: isUploading } = usePostApiV1PersonIdAttachments({
    mutation: { onSuccess: invalidate },
  });

  const { mutate: deleteAttachment, isPending: isDeleting } =
    useDeleteApiV1PersonIdAttachmentsAttachmentid({
      mutation: { onSuccess: invalidate },
    });

  const upload = (
    file: File,
    opts?: { classStudentId?: number; enrollmentDocumentId?: number },
    callbacks?: { onSuccess?: () => void },
  ) => {
    if (!personId) return;
    uploadAttachment(
      {
        id: personId,
        data: {
          File: file,
          ...(opts?.classStudentId !== undefined ? { ClassStudentId: opts.classStudentId } : {}),
          ...(opts?.enrollmentDocumentId !== undefined
            ? { EnrollmentDocumentId: opts.enrollmentDocumentId }
            : {}),
        },
      },
      callbacks?.onSuccess ? { onSuccess: callbacks.onSuccess } : {},
    );
  };

  const remove = (attachmentId: number) => {
    if (!personId) return;
    deleteAttachment({ id: personId, attachmentId });
  };

  return { attachments, isLoading, isError, isUploading, isDeleting, upload, remove } as const;
}
